import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  newUser, loadsysUsers, deleteUserUrl,
  rolesUrl, roleByIdUrl, permissionsUrl,
} from "../../BaseURL";

// Roles & their permission grants are loaded from the database (see the
// /roles + /permissions endpoints).  The hardcoded ROLE_PERMISSIONS map
// that used to live here is gone — admins control the catalogue from the
// "Role & Permissions" tab.
const INITIAL_FORM = { full_name: "", email: "", username: "", tel: "", password: "", role: "user" };

export default function UsersAccess() {
  // No auth header — the axios interceptor in App.js attaches x-user-id.

  // ── State ────────────────────────────────────────────────────────────────
  const [activeSection, setActiveSection] = useState("list");
  const [form,          setForm]          = useState(INITIAL_FORM);
  const [showPassword,  setShowPassword]  = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [fetching,      setFetching]      = useState(true);
  const [msg,           setMsg]           = useState({ type: "", text: "" });
  const [editUser,      setEditUser]      = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm,    setSearchTerm]    = useState("");
  const [users,         setUsers]         = useState([]);
  const [fetchError,    setFetchError]    = useState("");
  const [roles,         setRoles]         = useState([]);
  const [permissions,   setPermissions]   = useState([]);
  const [rolesLoading,  setRolesLoading]  = useState(true);

  // ── Load roles + permissions catalogue ───────────────────────────────────
  const loadRolesAndPermissions = useCallback(async () => {
    setRolesLoading(true);
    try {
      const [rRes, pRes] = await Promise.all([
        axios.get(rolesUrl),
        axios.get(permissionsUrl),
      ]);
      setRoles(rRes.data?.results || []);
      setPermissions(pRes.data?.results || []);
    } catch (err) {
      console.error("Failed to load roles/permissions:", err);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => { loadRolesAndPermissions(); }, [loadRolesAndPermissions]);

  // Index for quick lookups (name → row, name → permission keys).
  const roleByName = useMemo(() => {
    const map = new Map();
    for (const r of roles) map.set(r.name, r);
    return map;
  }, [roles]);

  // ── Load users from backend ──────────────────────────────────────────────
  const loadUsers = useCallback(async () => {
    setFetching(true);
    setFetchError("");
    try {
      const res = await axios.get(loadsysUsers);
      const data = res.data?.results || res.data?.users || [];
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load users:", err);
      setFetchError(
        err.response?.status === 401
          ? "Session expired. Please log in again."
          : err.response?.status === 403
          ? "Access denied. Super admin only."
          : "Could not load users from server."
      );
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // ── Filtered users ───────────────────────────────────────────────────────
  const filteredUsers = users.filter(u => {
    const name  = (u.full_name || u.name || "").toLowerCase();
    const email = (u.email || "").toLowerCase();
    const term  = searchTerm.toLowerCase();
    return name.includes(term) || email.includes(term);
  });

  // ── Helpers ───────────────────────────────────────────────────────────────
  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4500);
  };

  const validateForm = () => {
    if (!(form.full_name || "").trim())  return "Full name is required.";
    if (!form.email.trim() || !form.email.includes("@")) return "A valid email is required.";
    if (!(form.username || "").trim())   return "Username is required.";
    if (!editUser && !form.password)     return "Temporary password is required.";
    // No minimum here — the user will be forced to choose a strong password
    // of their own on first login.  See /first-login.
    if (!form.role) return "Pick a role to assign.";
    return null;
  };

  // ── Create user ───────────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    const error = validateForm();
    if (error) { showMsg("error", error); return; }

    setLoading(true);
    setMsg({ type: "", text: "" });
    try {
      const res = await axios.post(newUser, form);
      if (!res.data?.success) {
        showMsg("error", res.data?.message || "Failed to create user.");
        return;
      }
      showMsg("success", `User "${form.full_name}" created successfully.`);
      setForm(INITIAL_FORM);
      setEditUser(null);
      await loadUsers();
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Failed to create user.");
    } finally {
      setLoading(false);
    }
  };

  // ── Delete user ───────────────────────────────────────────────────────────
  const handleDelete = async (userId) => {
    try {
      const res = await axios.post(deleteUserUrl, { id: userId });
      if (!res.data?.success) {
        setDeleteConfirm(null);
        showMsg("error", res.data?.message || "Failed to remove user.");
        return;
      }
      setDeleteConfirm(null);
      showMsg("success", "User removed successfully.");
      await loadUsers();
    } catch (err) {
      setDeleteConfirm(null);
      showMsg("error", err.response?.data?.message || "Failed to remove user.");
    }
  };


  // ── Derived values ────────────────────────────────────────────────────────
  const getStatus = (u) => {
    if (u.status) return u.status;
    return u.is_active === 1 || u.is_active === true ? "active" : "inactive";
  };

  const getRole = (u) => u.role || "user";
  const getName = (u) => u.full_name || u.name || "—";

  const adminCount = users.filter(u => getRole(u) === "super_admin").length;

  // ── Styles ────────────────────────────────────────────────────────────────
  const msgStyle = {
    padding: "12px 16px", borderRadius: 8, fontSize: 13.5, marginBottom: 16, fontWeight: 400,
    background: msg.type === "success" ? "#eaf5ee" : "#fef0ee",
    color:      msg.type === "success" ? "#2d6a4f" : "#a93226",
    border:     `1px solid ${msg.type === "success" ? "#a8d5b5" : "#f5c5bc"}`,
    display: "flex", alignItems: "center", gap: 10,
  };

  const tabs = [
    { id: "list",   label: "User Accounts",      icon: "👥" },
    { id: "create", label: "Create User",         icon: "➕" },
    { id: "roles",  label: "Role & Permissions",  icon: "🔐" },
  ];

  // ── Skeleton loader ───────────────────────────────────────────────────────
  const SkeletonRow = () => (
    <tr>
      {[1,2,3,4,5,6].map(i => (
        <td key={i}>
          <div style={{ height: 14, borderRadius: 4, background: "rgba(180,140,80,.1)", animation: "fadeUp 1s infinite alternate" }}/>
        </td>
      ))}
    </tr>
  );

  return (
    <div>

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, color: "#2c1f14", marginBottom: 4, fontWeight: 500 }}>
          Users & Access Control
        </div>
        <div style={{ fontSize: 15, color: "#6b5142", fontWeight: 400 }}>
          Manage staff accounts, roles, and system permissions
        </div>
      </div>

      {/* ── Summary Stats ── */}
      <div className="g4" style={{ marginBottom: 24 }}>
        {[
          { icon: "👥", val: fetching ? "…" : users.length,       lbl: "Total Users",  sub: "All accounts" },
          { icon: "✅", val: fetching ? "…" : users.length,        lbl: "Active",        sub: "Currently enabled" },
          { icon: "⛔", val: fetching ? "…" : 0,      lbl: "Inactive",      sub: "Disabled accounts" },
          { icon: "🔐", val: fetching ? "…" : adminCount,         lbl: "Admins",        sub: "Full access" },
        ].map((s, i) => (
          <div className="stat" key={i} style={{ animationDelay: `${i * 0.06}s` }}>
            <div className="stat-icon">{s.icon}</div>
            <div className="stat-val">{s.val}</div>
            <div className="stat-lbl">{s.lbl}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {tabs.map(t => (
          <button key={t.id} className={`btn-outline ${activeSection === t.id ? "active" : ""}`}
            onClick={() => { setActiveSection(t.id); setMsg({ type: "", text: "" }); }}>
            {t.icon} {t.label}
          </button>
        ))}
        <button className="btn-outline" style={{ marginLeft: "auto" }} onClick={loadUsers} title="Refresh">
          🔄 Refresh
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════════
          SECTION 1 — USER LIST
      ══════════════════════════════════════════════════════════ */}
      {activeSection === "list" && (
        <div className="panel">
          <div className="panel-hd">
            <span className="panel-hd-title">System Accounts</span>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input
                className="f-input"
                style={{ width: 220, padding: "8px 12px", fontSize: 13 }}
                placeholder="🔍  Search users…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <button className="btn-dark" onClick={() => { setActiveSection("create"); setEditUser(null); setForm(INITIAL_FORM); }}>
                + Create User
              </button>
            </div>
          </div>

          {/* Fetch error */}
          {fetchError && (
            <div style={{ margin: "14px 18px", padding: "12px 16px", borderRadius: 8, background: "#fef0ee", color: "#a93226", border: "1px solid #f5c5bc", fontSize: 13.5, fontWeight: 400 }}>
              ⚠️ {fetchError}
              <button onClick={loadUsers} style={{ marginLeft: 12, background: "none", border: "none", color: "#a93226", cursor: "pointer", textDecoration: "underline", fontSize: 13 }}>
                Retry
              </button>
            </div>
          )}

          {/* Success/error from actions */}
          {msg.text && (
            <div style={{ ...msgStyle, margin: "14px 18px 0" }}>
              {msg.type === "success" ? "✅" : "⚠️"} {msg.text}
            </div>
          )}

          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Role</th>
                  
                  <th>Last Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {fetching ? (
                  [1,2,3].map(i => <SkeletonRow key={i} />)
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: 40, color: "#6b5142" }}>
                      {searchTerm ? "No users match your search." : "No users found."}
                    </td>
                  </tr>
                ) : filteredUsers.map(u => {
                  const uid  = u.id || u.trans_code;
                  const role = getRole(u);
                  const name = getName(u);
                  return (
                    <tr key={uid}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div className="avatar" style={{ width: 38, height: 38, fontSize: 17 }}>
                            {name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 400, fontSize: 14, color: "#2c1f14" }}>{name}</div>
                            {u.staff_id && <div style={{ fontSize: 11, color: "#6b5142" }}>ID: {u.staff_id}</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ color: "#6b5142", fontSize: 13 }}>{u.email || u.emain || "—"}</td>
                      <td>
                        <span className={`badge ${role === "super_admin" ? "badge-gold" : "badge-green"}`}>
                          {role}
                        </span>
                      </td>
                     
                      <td style={{ color: "#6b5142", fontSize: 13 }}>
                        {u.last || u.created_at
                          ? new Date(u.created_at || Date.now()).toLocaleDateString("en-GB", { day:"numeric", month:"short" })
                          : "—"}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button className="btn-outline" style={{ padding: "5px 12px", fontSize: 11 }}
                            onClick={() => {
                              setEditUser(u);
                              setActiveSection("create");
                              setForm({ full_name: name, email: u.email || u.emain || "", password: "", role });
                            }}>
                            Edit
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(u)}
                            style={{ padding: "5px 12px", fontSize: 11, background: "transparent", border: "1.5px solid rgba(192,57,43,.25)", borderRadius: 7, color: "#a93226", cursor: "pointer", fontFamily: "'Jost',sans-serif", letterSpacing: ".08em", textTransform: "uppercase", transition: "all .2s" }}
                            onMouseEnter={e => e.currentTarget.style.background = "rgba(192,57,43,.07)"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer count */}
          {!fetching && !fetchError && (
            <div style={{ padding: "10px 18px", fontSize: 12, color: "#6b5142", borderTop: "1px solid rgba(180,140,80,.1)" }}>
              Showing {filteredUsers.length} of {users.length} users
              {searchTerm && ` matching "${searchTerm}"`}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          SECTION 2 — CREATE / EDIT USER
      ══════════════════════════════════════════════════════════ */}
      {activeSection === "create" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 20 }}>

          <div className="panel">
            <div className="panel-hd">
              <span className="panel-hd-title">
                {editUser ? `Edit — ${getName(editUser)}` : "Create New User Account"}
              </span>
              {editUser && (
                <button className="btn-outline" style={{ fontSize: 11, padding: "5px 12px" }}
                  onClick={() => { setEditUser(null); setForm(INITIAL_FORM); setMsg({ type:"", text:"" }); }}>
                  ✕ Cancel Edit
                </button>
              )}
            </div>

            <div style={{ padding: 24 }}>
              {msg.text && (
                <div style={msgStyle}>
                  {msg.type === "success" ? "✅" : "⚠️"} {msg.text}
                </div>
              )}

              <form onSubmit={handleCreate}>
                <div className="f-group">
                  <label className="f-label">Full Name *</label>
                  <input className="f-input" placeholder="e.g. Kwame Mensah"
                    value={form.full_name}
                    onChange={e => setForm({ ...form, full_name: e.target.value })} required/>
                </div>

                <div className="f-group">
                  <label className="f-label">Email Address *</label>
                  <input className="f-input" type="email" placeholder="name@hotel.com"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })} required/>
                </div>

                <div className="f-group">
                  <label className="f-label">Username *</label>
                  <input className="f-input" placeholder="login username (no spaces)"
                    value={form.username}
                    onChange={e => setForm({ ...form, username: e.target.value })}
                    autoComplete="off"
                    required/>
                  <div style={{ fontSize: 12, color: "#6b5142", marginTop: 4, fontWeight: 400 }}>
                    Users sign in with this username, not the email.
                  </div>
                </div>

                <div className="f-group">
                  <label className="f-label">{editUser ? "New Password (leave blank to keep current)" : "Temporary Password *"}</label>
                  <div style={{ position: "relative" }}>
                    <input className="f-input"
                      type={showPassword ? "text" : "password"}
                      placeholder={editUser ? "Leave blank to keep current" : "Any value — the user will replace it on first login"}
                      value={form.password}
                      onChange={e => setForm({ ...form, password: e.target.value })}
                      required={!editUser}
                      style={{ paddingRight: 48 }}/>
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#6b5142", fontSize: 16 }}>
                      {showPassword ? "🙈" : "👁"}
                    </button>
                  </div>
                  {!editUser && (
                    <div style={{ marginTop: 6, fontSize: 12, color: "#6b5142", fontWeight: 400 }}>
                      No strength rules here — this is a temporary password.
                      The user will be required to set their own strong
                      password the first time they sign in.
                    </div>
                  )}
                </div>

                <div className="f-group">
                  <label className="f-label">Role & Access Level *</label>
                  <select
                    className="f-select"
                    value={form.role}
                    onChange={e => setForm({ ...form, role: e.target.value })}
                  >
                    {rolesLoading
                      ? <option>Loading roles…</option>
                      : roles
                          // super_admin can never be assigned to new users
                          .filter(r => r.name !== "super_admin")
                          .map(r => (
                            <option key={r.id} value={r.name}>
                              {r.display_name}{r.description ? ` — ${r.description.slice(0, 64)}` : ""}
                            </option>
                          ))}
                  </select>
                </div>

                {/* Live preview of the selected role's permissions. */}
                <div style={{ background: "rgba(200,160,90,.06)", border: "1px solid rgba(200,160,90,.2)", borderRadius: 8, padding: "14px 16px", marginBottom: 18 }}>
                  <div style={{ fontSize: 12, color: "#c8a05a", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 10, fontWeight: 500 }}>
                    👤 {roleByName.get(form.role)?.display_name || form.role} Permissions
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px" }}>
                    {(roleByName.get(form.role)?.permission_keys || []).map((key) => {
                      const p = permissions.find(x => x.key === key);
                      return (
                        <div key={key} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, color: "#3a2a1a", fontWeight: 400 }}>
                          <span style={{ color: "#4a7c59", fontSize: 12 }}>✓</span> {p?.label || key}
                        </div>
                      );
                    })}
                    {(roleByName.get(form.role)?.permission_keys || []).length === 0 && (
                      <div style={{ fontSize: 12.5, color: "#6b5142", fontStyle: "italic", gridColumn: "1 / -1" }}>
                        No permissions assigned yet — edit this role in the "Role &amp; Permissions" tab.
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10 }}>
                  <button className="btn-dark" type="submit" disabled={loading} style={{ flex: 1, opacity: loading ? 0.6 : 1 }}>
                    {loading ? "Saving…" : editUser ? "Save Changes" : "Create Account"}
                  </button>
                  <button type="button" className="btn-outline"
                    onClick={() => { setForm(INITIAL_FORM); setEditUser(null); setMsg({ type:"", text:"" }); }}>
                    Reset
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Tips + recent */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="panel">
              <div className="panel-hd"><span className="panel-hd-title">Account Tips</span></div>
              <div style={{ padding: 18 }}>
                {[
                  { icon:"🔐", title:"Admin role",       desc:"Only assign to trusted senior staff. Admins can create and delete other users." },
                  { icon:"📧", title:"Email address",    desc:"Must be unique. Staff use this to log in to the system." },
                  { icon:"🔑", title:"Strong passwords", desc:"Use at least 8 characters, mix letters and numbers for better security." },
                  { icon:"⛔", title:"Deactivation",     desc:"Disable accounts instead of deleting to preserve audit history." },
                ].map((tip, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: i < 3 ? "1px solid rgba(180,140,80,.08)" : "none" }}>
                    <span style={{ fontSize: 20, flexShrink: 0 }}>{tip.icon}</span>
                    <div>
                      <div style={{ fontSize: 13.5, fontWeight: 400, color: "#2c1f14", marginBottom: 3 }}>{tip.title}</div>
                      <div style={{ fontSize: 12.5, color: "#6b5142", fontWeight: 400, lineHeight: 1.5 }}>{tip.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="panel">
              <div className="panel-hd"><span className="panel-hd-title">Recent Accounts</span></div>
              {fetching ? (
                <div style={{ padding: "20px 16px", color: "#6b5142", fontSize: 13 }}>Loading…</div>
              ) : users.slice(-3).reverse().map(u => {
                const uid = u.id || u.trans_code;
                return (
                  <div key={uid} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 16px", borderBottom: "1px solid rgba(180,140,80,.07)" }}>
                    <div className="avatar" style={{ width: 32, height: 32, fontSize: 14 }}>
                      {getName(u).charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: "#2c1f14", fontWeight: 400 }}>{getName(u)}</div>
                      <div style={{ fontSize: 11, color: "#6b5142" }}>{u.email || u.emain || "—"}</div>
                    </div>
                    <span className={`badge ${getRole(u) === "super_admin" ? "badge-gold" : "badge-green"}`}>
                      {getRole(u)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          SECTION 3 — ROLES & PERMISSIONS (DB-backed, editable)
      ══════════════════════════════════════════════════════════ */}
      {activeSection === "roles" && (
        <RolesAndPermissionsPanel
          roles={roles}
          permissions={permissions}
          rolesLoading={rolesLoading}
          users={users}
          getName={getName}
          getStatus={getStatus}
          onChange={loadRolesAndPermissions}
          showMsg={showMsg}
        />
      )}

      {/* ══════════════════════════════════════════════════════════
          DELETE CONFIRMATION MODAL
      ══════════════════════════════════════════════════════════ */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div style={{ background: "#fdf8f2", borderRadius: 12, width: "min(420px, 90vw)", overflow: "hidden", border: "1px solid rgba(180,140,80,.2)", animation: "fadeUp .2s both" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ background: "#2c1f14", padding: "18px 24px", display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 22 }}>⚠️</span>
              <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, color: "#f0e4cf" }}>Remove User</span>
            </div>
            <div style={{ padding: 24 }}>
              <p style={{ fontSize: 14.5, color: "#3a2a1a", fontWeight: 400, lineHeight: 1.6, marginBottom: 20 }}>
                Are you sure you want to remove <strong style={{ fontWeight: 500 }}>{getName(deleteConfirm)}</strong>?
                This will revoke their system access immediately.
              </p>
              <div style={{ background: "rgba(192,57,43,.05)", border: "1px solid rgba(192,57,43,.15)", borderRadius: 7, padding: "10px 14px", fontSize: 13, color: "#8a3226", fontWeight: 400, marginBottom: 20 }}>
                💡 Consider disabling the account instead to preserve activity history.
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => handleDelete(deleteConfirm.id || deleteConfirm.trans_code)}
                  style={{ flex: 1, padding: 11, background: "#a93226", color: "#fff", border: "none", borderRadius: 7, fontFamily: "'Jost',sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: ".14em", textTransform: "uppercase", cursor: "pointer" }}>
                  Yes, Remove
                </button>
                <button className="btn-outline" style={{ flex: 1 }} onClick={() => setDeleteConfirm(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Roles & Permissions panel
//
//  Lists every role from the database, lets the super_admin:
//    • create a new role (slug auto-derived from display name)
//    • toggle permissions on/off and save (replaces the role's grant set)
//    • delete a non-system role (blocked if any users still have it)
//
//  Permissions are grouped by category so the editor stays readable as the
//  catalogue grows.
// ─────────────────────────────────────────────────────────────────────────────
function RolesAndPermissionsPanel({
  roles, permissions, rolesLoading, users,
  getName, getStatus, onChange, showMsg,
}) {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ display_name: "", description: "", permission_ids: [] });
  const [savingNew, setSavingNew] = useState(false);
  const [busyRoleId, setBusyRoleId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Local edit buffers keyed by role id, so toggling a permission doesn't
  // mutate the prop until the user presses Save.
  const [editBuffer, setEditBuffer] = useState({});

  const seedBuffer = (role) => {
    if (editBuffer[role.id]) return;
    setEditBuffer((b) => ({
      ...b,
      [role.id]: {
        display_name: role.display_name,
        description: role.description || "",
        permission_ids: [...(role.permission_ids || [])],
      },
    }));
  };

  const togglePerm = (roleId, permId) => {
    setEditBuffer((b) => {
      const cur = b[roleId];
      if (!cur) return b;
      const has = cur.permission_ids.includes(permId);
      return {
        ...b,
        [roleId]: {
          ...cur,
          permission_ids: has
            ? cur.permission_ids.filter(x => x !== permId)
            : [...cur.permission_ids, permId],
        },
      };
    });
  };

  const groupedPerms = useMemo(() => {
    const groups = new Map();
    for (const p of permissions) {
      if (!groups.has(p.category)) groups.set(p.category, []);
      groups.get(p.category).push(p);
    }
    return Array.from(groups.entries());
  }, [permissions]);

  const saveRole = async (role) => {
    const buf = editBuffer[role.id];
    if (!buf) return;
    setBusyRoleId(role.id);
    try {
      const res = await axios.put(roleByIdUrl(role.id), buf);
      if (!res.data?.success) {
        showMsg("error", res.data?.message || "Could not save role");
        return;
      }
      showMsg("success", `Saved "${buf.display_name}".`);
      setEditBuffer((b) => { const copy = { ...b }; delete copy[role.id]; return copy; });
      await onChange();
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Could not save role");
    } finally {
      setBusyRoleId(null);
    }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    if (!draft.display_name.trim()) { showMsg("error", "Give the role a name."); return; }
    setSavingNew(true);
    try {
      const res = await axios.post(rolesUrl, draft);
      if (!res.data?.success) {
        showMsg("error", res.data?.message || "Could not create role");
        return;
      }
      showMsg("success", `Created "${draft.display_name}".`);
      setDraft({ display_name: "", description: "", permission_ids: [] });
      setCreating(false);
      await onChange();
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Could not create role");
    } finally {
      setSavingNew(false);
    }
  };

  const deleteRole = async (role) => {
    setBusyRoleId(role.id);
    try {
      const res = await axios.delete(roleByIdUrl(role.id));
      if (!res.data?.success) {
        showMsg("error", res.data?.message || "Could not delete role");
        return;
      }
      showMsg("success", `Deleted "${role.display_name}".`);
      setConfirmDelete(null);
      await onChange();
    } catch (err) {
      showMsg("error", err.response?.data?.message || "Could not delete role");
    } finally {
      setBusyRoleId(null);
    }
  };

  return (
    <>
      {/* ── Header strip with create-role action ── */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-hd">
          <span className="panel-hd-title">Roles & Permissions</span>
          <button className="btn-dark" onClick={() => setCreating(c => !c)}>
            {creating ? "✕ Cancel" : "+ New Role"}
          </button>
        </div>

        {creating && (
          <form onSubmit={submitNew} style={{ padding: 18, borderTop: "1px solid rgba(180,140,80,.1)" }}>
            <div className="f-group">
              <label className="f-label">Role Name *</label>
              <input
                className="f-input"
                placeholder="e.g. Front Desk Supervisor"
                value={draft.display_name}
                onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
                required
              />
            </div>
            <div className="f-group">
              <label className="f-label">Description (optional)</label>
              <input
                className="f-input"
                placeholder="What does this role do?"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </div>
            <div className="f-group">
              <label className="f-label">Permissions</label>
              <PermissionGrid
                groupedPerms={groupedPerms}
                selected={draft.permission_ids}
                onToggle={(id) =>
                  setDraft(d => ({
                    ...d,
                    permission_ids: d.permission_ids.includes(id)
                      ? d.permission_ids.filter(x => x !== id)
                      : [...d.permission_ids, id],
                  }))
                }
              />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn-dark" type="submit" disabled={savingNew} style={{ flex: 1, opacity: savingNew ? 0.6 : 1 }}>
                {savingNew ? "Creating…" : "Create Role"}
              </button>
              <button type="button" className="btn-outline" onClick={() => setCreating(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* ── Role cards ── */}
      {rolesLoading ? (
        <div className="panel" style={{ padding: 18, color: "#6b5142", fontSize: 13 }}>
          Loading roles…
        </div>
      ) : (
        <div className="g2">
          {roles.map(role => {
            const buf = editBuffer[role.id];
            const isEditing = !!buf;
            const selected = buf?.permission_ids || role.permission_ids || [];
            const usersForRole = users.filter(u => (u.role || "user") === role.name);

            return (
              <div className="panel" key={role.id}>
                <div className="panel-hd">
                  <span className="panel-hd-title">
                    {role.name === "super_admin" ? "🔐" : "👤"} {role.display_name}
                    {role.is_system === 1 && (
                      <span style={{ marginLeft: 8, fontSize: 10, padding: "2px 6px", background: "rgba(200,160,90,.18)", color: "#8a6a30", borderRadius: 4, letterSpacing: ".08em" }}>
                        SYSTEM
                      </span>
                    )}
                  </span>
                  <span className={`badge ${role.name === "super_admin" ? "badge-gold" : "badge-green"}`}>
                    {role.user_count} {role.user_count === 1 ? "user" : "users"}
                  </span>
                </div>

                <div style={{ padding: "8px 18px 16px" }}>
                  {role.description && (
                    <div style={{ fontSize: 13, color: "#6b5142", fontWeight: 400, marginBottom: 14, lineHeight: 1.6 }}>
                      {role.description}
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                    {!isEditing ? (
                      <button className="btn-outline" style={{ fontSize: 11, padding: "6px 14px" }} onClick={() => seedBuffer(role)}>
                        Edit Permissions
                      </button>
                    ) : (
                      <>
                        <button
                          className="btn-dark"
                          style={{ fontSize: 11, padding: "6px 14px" }}
                          onClick={() => saveRole(role)}
                          disabled={busyRoleId === role.id}
                        >
                          {busyRoleId === role.id ? "Saving…" : "Save"}
                        </button>
                        <button
                          className="btn-outline"
                          style={{ fontSize: 11, padding: "6px 14px" }}
                          onClick={() =>
                            setEditBuffer(b => { const copy = { ...b }; delete copy[role.id]; return copy; })
                          }
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    {role.is_system !== 1 && !isEditing && (
                      <button
                        onClick={() => setConfirmDelete(role)}
                        style={{ marginLeft: "auto", padding: "6px 14px", fontSize: 11, background: "transparent", border: "1.5px solid rgba(192,57,43,.25)", borderRadius: 7, color: "#a93226", cursor: "pointer", fontFamily: "'Jost',sans-serif", letterSpacing: ".08em", textTransform: "uppercase" }}
                      >
                        Delete
                      </button>
                    )}
                  </div>

                  <div style={{ fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase", color: "#6b5142", marginBottom: 10 }}>
                    Permissions
                  </div>
                  <PermissionGrid
                    groupedPerms={groupedPerms}
                    selected={selected}
                    onToggle={isEditing ? (pid) => togglePerm(role.id, pid) : null}
                    readOnly={!isEditing}
                  />

                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase", color: "#6b5142", marginBottom: 10 }}>
                      Users with this role
                    </div>
                    {usersForRole.length === 0 ? (
                      <div style={{ color: "#6b5142", fontSize: 13 }}>No users with this role.</div>
                    ) : usersForRole.map(u => {
                      const uid = u.id || u.trans_code;
                      return (
                        <div key={uid} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid rgba(180,140,80,.07)" }}>
                          <div className="avatar" style={{ width: 30, height: 30, fontSize: 13 }}>
                            {getName(u).charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13.5, color: "#2c1f14", fontWeight: 400 }}>{getName(u)}</div>
                            <div style={{ fontSize: 12, color: "#6b5142" }}>{u.email || u.emain || "—"}</div>
                          </div>
                          <span className={`badge ${getStatus(u) === "active" ? "badge-green" : "badge-gray"}`}>
                            {getStatus(u)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Confirm role delete ── */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div
            style={{ background: "#fdf8f2", borderRadius: 12, width: "min(420px, 90vw)", overflow: "hidden", border: "1px solid rgba(180,140,80,.2)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ background: "#2c1f14", padding: "18px 24px", display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 22 }}>⚠️</span>
              <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, color: "#f0e4cf" }}>
                Delete role
              </span>
            </div>
            <div style={{ padding: 24 }}>
              <p style={{ fontSize: 14.5, color: "#3a2a1a", fontWeight: 400, lineHeight: 1.6, marginBottom: 20 }}>
                Delete <strong>{confirmDelete.display_name}</strong>? This cannot be undone.
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => deleteRole(confirmDelete)}
                  disabled={busyRoleId === confirmDelete.id}
                  style={{ flex: 1, padding: 11, background: "#a93226", color: "#fff", border: "none", borderRadius: 7, fontFamily: "'Jost',sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: ".14em", textTransform: "uppercase", cursor: "pointer", opacity: busyRoleId === confirmDelete.id ? 0.6 : 1 }}
                >
                  {busyRoleId === confirmDelete.id ? "Deleting…" : "Yes, Delete"}
                </button>
                <button className="btn-outline" style={{ flex: 1 }} onClick={() => setConfirmDelete(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Two-column grid of permissions grouped by category.  Each row is a
// proper "chip" with a clearly visible 18px checkbox + a hover state, so
// admins can see at a glance which permissions are on or off without
// squinting at hairline native checkboxes.
function PermissionGrid({ groupedPerms, selected, onToggle, readOnly = false }) {
  const isSelected = (id) => selected.includes(id);
  return (
    <div>
      {groupedPerms.map(([category, perms]) => (
        <div key={category} style={{ marginBottom: 18 }}>
          <div style={{
            fontSize: 11.5,
            letterSpacing: ".18em",
            textTransform: "uppercase",
            color: "#6b5142",
            marginBottom: 10,
            fontWeight: 600,
          }}>
            {category}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 14px" }}>
            {perms.map(p => {
              const on = isSelected(p.id);
              return (
                <label
                  key={p.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    fontSize: 14,
                    color: on ? "#2c1f14" : "#3a2a1a",
                    fontWeight: on ? 500 : 400,
                    cursor: readOnly ? "default" : "pointer",
                    padding: "9px 12px",
                    borderRadius: 8,
                    border: `1.5px solid ${on ? "rgba(200,160,90,.55)" : "rgba(180,140,80,.18)"}`,
                    background: on ? "rgba(200,160,90,.10)" : "rgba(253,248,242,.6)",
                    transition: "background .15s, border-color .15s",
                  }}
                  onMouseEnter={(e) => {
                    if (readOnly) return;
                    e.currentTarget.style.background = on ? "rgba(200,160,90,.16)" : "rgba(200,160,90,.06)";
                  }}
                  onMouseLeave={(e) => {
                    if (readOnly) return;
                    e.currentTarget.style.background = on ? "rgba(200,160,90,.10)" : "rgba(253,248,242,.6)";
                  }}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={onToggle ? () => onToggle(p.id) : undefined}
                    disabled={readOnly}
                    style={{
                      accentColor: "#c8a05a",
                      width: 18,
                      height: 18,
                      flexShrink: 0,
                      cursor: readOnly ? "default" : "pointer",
                    }}
                  />
                  <span>{p.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
